import { usePageMeta } from "@/lib/use-page-meta";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { MapView } from "@/components/map-view";
import { videoBySlugQuery, videoParam, relatedCandidatesQuery, videosByYoutuberQuery, type VideoWithLocations } from "@/lib/queries";
import { formatCompact, formatDate, formatDuration, translateCountry, cleanTitle } from "@/lib/format";
import { Youtube, MapPin, ArrowLeft, Flag } from "lucide-react";
import { ReportVideoDialog } from "@/components/report-video-dialog";
import { safeLoad } from "@/lib/ssr-safe";
import "@/i18n";


type TextMode = "summary" | "description" | "transcript";

export const Route = createFileRoute("/v/$slug")({
  staticData: { sitemap: true },
  head: ({ params, loaderData }) => {
    const pretty =
      (loaderData as { videoTitle?: string | null } | undefined)?.videoTitle ??
      params.slug
        .replace(/-\d{4}-\d{2}-\d{2}$/, "")
        .replace(/-/g, " ")
        .replace(/^./, (c) => c.toUpperCase());
    const title = `${pretty} · Youtubers Map`;
    const description = `Localitzacions, resum i dades del vídeo: ${pretty}.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "video.other" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  loader: async ({ context, params }) => {
    // Awaited so the video page is server-rendered with real content (SEO).
    const v = await safeLoad("video:bySlug", context.queryClient.ensureQueryData(videoBySlugQuery(params.slug)));
    return { videoTitle: v?.title ?? null };
  },
  component: VideoPage,
});

function VideoPage() {
  const { slug } = Route.useParams();
  return (
    <div className="min-h-screen">
      <SiteNav />
      <Suspense
        fallback={<div className="max-w-5xl mx-auto px-6 py-24 text-center text-mono-caps text-ink/40">…</div>}
      >
        <VideoContent slug={slug} />
      </Suspense>
      <SiteFooter />
    </div>
  );
}

function VideoContent({ slug }: { slug: string }) {
  const { data: v } = useSuspenseQuery(videoBySlugQuery(slug));
  const { t, i18n } = useTranslation();
  if (!v) throw notFound();
  usePageMeta(t("meta.videoTitle", { name: v.title }), t("meta.videoDesc", { name: v.title }));

  const ytUrl = `https://youtube.com/watch?v=${v.youtube_video_id}`;
  const locWithCoords = v.video_locations.filter((l) => l.latitude != null && l.longitude != null);
  const pins = locWithCoords.map((l) => ({
    id: l.id,
    lat: l.latitude!,
    lng: l.longitude!,
    color: l.is_primary ? "oklch(0.62 0.15 55)" : "oklch(0.55 0.10 55)",
    label: l.name ?? l.city ?? l.region ?? translateCountry(l.country_code, l.country_name, i18n.language),
    subtitle: [l.city, l.region, translateCountry(l.country_code, l.country_name, i18n.language)].filter(Boolean).join(", ") || undefined,
    countryCode: l.country_code,
  }));

  const defaultLocId =
    v.video_locations.find((l) => l.is_primary)?.id ?? v.video_locations[0]?.id ?? null;
  const [selectedLocId, setSelectedLocId] = useState<string | null>(defaultLocId);
  const [hoveredLocId, setHoveredLocId] = useState<string | null>(null);
  const activeLocId = hoveredLocId ?? selectedLocId;
  const handlePinClick = useCallback((pid: string) => setSelectedLocId(pid), []);
  const handlePinHover = useCallback((pid: string | null) => setHoveredLocId(pid), []);


  const summary = v.transcript_summary?.trim() || null;
  const descClean = v.description_clean?.trim() || null;
  const descOriginal = v.description?.trim() || null;
  const transcript = (v as any).transcript?.trim?.() || null;
  const transcriptOriginal = (v as any).transcript_original?.trim?.() || null;

  const availability: Record<TextMode, string | null> = {
    summary,
    description: descClean ?? descOriginal,
    transcript,
  };

  const modes: TextMode[] = ["summary", "description", "transcript"];
  const firstAvailable = (modes.find((m) => availability[m]) ?? "summary") as TextMode;
  const [mode, setMode] = useState<TextMode>(firstAvailable);
  const [showOriginal, setShowOriginal] = useState(false);
  const [transcriptOrig, setTranscriptOrig] = useState(false);
  const currentText =
    mode === "description" && showOriginal
      ? descOriginal
      : mode === "transcript" && transcriptOrig && transcriptOriginal
      ? transcriptOriginal
      : availability[mode];

  const labelFor = (m: TextMode) => {
    switch (m) {
      case "summary": return t("common.summary");
      case "description": return t("common.description");
      case "transcript": return t("common.transcript");
    }
  };

  return (
    <>
      <section className="max-w-5xl mx-auto px-6 pt-10 pb-6 border-b border-ink/8">
        {v.youtubers && (
          <Link
            to="/y/$handle"
            params={{ handle: v.youtubers.creators?.slug ?? v.youtubers.handle }}
            className="inline-flex items-center gap-2 ring-hairline bg-paper px-3 py-1.5 text-sm font-medium text-ink/80 hover:text-paper hover:bg-ink transition-colors"
          >
            <ArrowLeft className="size-4" />
            {v.youtubers.avatar_url && (
              <img src={v.youtubers.avatar_url} alt="" className="size-5 rounded-full object-cover" />
            )}
            <span>{t("common.backTo", { name: v.youtubers.name })}</span>
          </Link>
        )}

        <h1 className="text-3xl md:text-4xl leading-tight mt-4 font-serif">{cleanTitle(v.title)}</h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 text-mono-caps text-ink/50">
          <span>{formatDate(v.published_at, i18n.language)}</span>
          <span>·</span>
          <span>{formatCompact(v.view_count)} {t("common.views").toLowerCase()}</span>
          {v.duration_seconds != null && (
            <>
              <span>·</span>
              <span>{formatDuration(v.duration_seconds)}</span>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-5">
          <a
            href={ytUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-ink text-paper px-4 py-2 text-sm font-medium hover:bg-amber transition-colors"
          >
            <Youtube className="size-4" /> {t("common.watchOnYoutube")}
          </a>
          <ReportVideoDialog
            videoId={v.id}
            trigger={
              <button
                type="button"
                className="inline-flex items-center gap-2 ring-hairline bg-paper text-ink/70 px-4 py-2 text-sm font-medium hover:bg-secondary hover:text-ink transition-colors"
              >
                <Flag className="size-4" /> {t("report.cta")}
              </button>
            }
          />
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-6">
          {v.thumbnail_url && (
            <a href={ytUrl} target="_blank" rel="noreferrer" className="block aspect-video bg-secondary overflow-hidden ring-hairline">
              <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
            </a>
          )}
          <div>
            <div className="flex flex-wrap gap-1 mb-4 ring-hairline bg-paper w-fit">
              {modes.map((m) => {
                const has = !!availability[m];
                const active = mode === m;
                return (
                  <button
                    key={m}
                    disabled={!has}
                    onClick={() => setMode(m)}
                    className={`text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 transition-colors ${
                      active
                        ? "bg-ink text-paper"
                        : has
                        ? "text-ink/60 hover:bg-secondary"
                        : "text-ink/20 cursor-not-allowed"
                    }`}
                  >
                    {labelFor(m)}
                  </button>
                );
              })}
            </div>
            {mode === "description" && descOriginal && descClean && (
              <p className="leading-relaxed whitespace-pre-line text-ink/70 text-sm">
                {showOriginal ? descOriginal : descClean}
                <button
                  onClick={() => setShowOriginal((s) => !s)}
                  className="ml-2 inline text-amber underline decoration-amber/40 hover:decoration-amber transition-all"
                >
                  {showOriginal ? t("common.viewCleanDescription") : t("common.viewOriginalDescription")}
                </button>
              </p>
            )}
            {mode === "description" && !(descOriginal && descClean) && (
              <p className="leading-relaxed whitespace-pre-line text-ink/70 text-sm">
                {currentText || "—"}
              </p>
            )}
            {mode === "transcript" && transcript && transcriptOriginal && (
              <div className="flex items-center gap-1 mb-3">
                {[
                  { orig: false, label: t("common.transcriptEnglish") },
                  { orig: true, label: t("common.transcriptOriginal") },
                ].map((opt) => (
                  <button
                    key={String(opt.orig)}
                    onClick={() => setTranscriptOrig(opt.orig)}
                    className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 ring-hairline transition-colors ${
                      transcriptOrig === opt.orig ? "bg-ink text-paper" : "text-ink/60 hover:bg-secondary"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
            {mode !== "description" && (
              <p className={`leading-relaxed whitespace-pre-line ${mode === "summary" ? "text-ink/80" : "text-ink/70 text-sm"}`}>
                {currentText || "—"}
              </p>
            )}
          </div>
        </div>

        <aside className="space-y-6">
          <div>
            <h2 className="text-mono-caps text-ink/50 mb-3">
              {t("common.locations")} ({v.video_locations.length})
            </h2>
            {pins.length > 0 && (
              <div className="h-72 bg-secondary ring-hairline overflow-hidden mb-3">
                <MapView
                  pins={pins}
                  className="w-full h-full"
                  fitToPins
                  interactive
                  activeId={activeLocId}
                  onPinClick={handlePinClick}
                  onPinHover={handlePinHover}
                />
              </div>
            )}
            <ul className="divide-y divide-ink/8 ring-hairline bg-paper">
              {v.video_locations.map((l) => {
                const country = translateCountry(l.country_code, l.country_name, i18n.language);
                const primaryLabel = l.name ?? l.city ?? l.region ?? country;
                const meta = [l.city, l.region, country]
                  .filter((s) => s && s !== primaryLabel)
                  .join(" · ");
                const active = activeLocId === l.id;
                return (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedLocId(l.id)}
                      onMouseEnter={() => setHoveredLocId(l.id)}
                      onMouseLeave={() => setHoveredLocId(null)}
                      className={`w-full text-left p-3 text-sm flex items-start gap-2.5 transition-colors ${
                        active ? "bg-amber/10" : "hover:bg-secondary/60"
                      }`}
                    >
                      <MapPin
                        className={`size-4 flex-shrink-0 mt-0.5 ${
                          active ? "text-amber fill-amber/30" : "text-ink/40"
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className={`font-serif ${active ? "text-amber-deep" : ""}`}>
                          {primaryLabel}
                        </div>
                        {meta && (
                          <div className="text-mono-caps text-ink/40 mt-0.5 text-[10px]">{meta}</div>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
              {v.video_locations.length === 0 && (
                <li className="p-3 text-sm text-ink/50">{t("common.noLocations")}</li>
              )}
            </ul>
          </div>
        </aside>
      </section>

      <RelatedVideos video={v} />
    </>
  );

}

function distanceKm(a: [number, number], b: [number, number]) {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const lat1 = (a[0] * Math.PI) / 180;
  const lat2 = (b[0] * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function RelatedVideos({ video }: { video: VideoWithLocations }) {
  const { t } = useTranslation();
  const countryCodes = useMemo(
    () =>
      Array.from(
        new Set(
          video.video_locations
            .map((l) => l.country_code)
            .filter((c): c is string => !!c),
        ),
      ),
    [video],
  );
  const origins = useMemo(
    () =>
      video.video_locations
        .filter((l) => l.latitude != null && l.longitude != null)
        .map((l) => [l.latitude!, l.longitude!] as [number, number]),
    [video],
  );

  const { data: candidates } = useQuery({
    ...relatedCandidatesQuery(countryCodes),
    enabled: countryCodes.length > 0,
  });
  // Fallback pool so the "same youtuber" block is never empty on rarely-visited countries.
  const { data: channelVideos } = useQuery(videosByYoutuberQuery(video.youtuber_id));

  const { same, others } = useMemo(() => {
    const score = (c: VideoWithLocations) => {
      let best = Number.POSITIVE_INFINITY;
      for (const l of c.video_locations) {
        if (l.latitude == null || l.longitude == null) continue;
        for (const o of origins) {
          const d = distanceKm(o, [l.latitude, l.longitude]);
          if (d < best) best = d;
        }
      }
      return best;
    };
    const scored = (candidates ?? [])
      .filter((c) => c.id !== video.id)
      .map((c) => ({ v: c, d: score(c) }))
      .sort((a, b) => a.d - b.d);

    const same = scored.filter((s) => s.v.youtuber_id === video.youtuber_id).slice(0, 4);
    if (same.length < 4) {
      const seen = new Set([video.id, ...same.map((s) => s.v.id)]);
      const extra = (channelVideos ?? [])
        .filter((c) => !seen.has(c.id))
        .map((c) => ({ v: { ...c, youtubers: (video as any).youtubers } as VideoWithLocations, d: score(c) }))
        .sort((a, b) => a.d - b.d)
        .slice(0, 4 - same.length);
      same.push(...extra);
    }
    return {
      same,
      others: scored.filter((s) => s.v.youtuber_id !== video.youtuber_id).slice(0, 4),
    };
  }, [candidates, channelVideos, origins, video]);


  if (same.length === 0 && others.length === 0) return null;

  const Card = ({ v }: { v: VideoWithLocations }) => {
    const yt = (v as any).youtubers as { handle: string; name: string; avatar_url: string | null } | null;
    return (
      <Link
        to="/v/$slug"
        params={{ slug: videoParam(v) }}
        className="group block ring-hairline bg-paper hover:bg-secondary/50 transition-colors"
      >
        <div className="aspect-video bg-secondary overflow-hidden">
          {v.thumbnail_url && (
            <img src={v.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" />
          )}
        </div>
        <div className="p-3">
          <div className="font-serif text-sm leading-snug line-clamp-2 group-hover:text-amber-deep transition-colors">
            {cleanTitle(v.title)}
          </div>
          <div className="text-mono-caps text-ink/40 mt-1.5 text-[10px] flex items-center gap-1.5">
            {yt?.avatar_url && <img src={yt.avatar_url} alt="" className="size-4 rounded-full object-cover" />}
            <span className="truncate">{yt?.name}</span>
            <span>·</span>
            <span>{formatCompact(v.view_count)}</span>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <section className="max-w-5xl mx-auto px-6 py-12 border-t border-ink/8">
      <h2 className="font-serif text-2xl mb-6">{t("common.relatedVideos")}</h2>
      {same.length > 0 && (
        <div className="mb-10">
          <h3 className="text-mono-caps text-ink/50 mb-3">{t("common.sameYoutuber")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {same.map(({ v }) => (
              <Card key={v.id} v={v} />
            ))}
          </div>
        </div>
      )}
      {others.length > 0 && (
        <div>
          <h3 className="text-mono-caps text-ink/50 mb-3">{t("common.otherYoutubers")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {others.map(({ v }) => (
              <Card key={v.id} v={v} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
